import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, DollarSign, Share2, Navigation, CheckCircle2, AlertCircle, Sparkles, ChevronRight, Bell, MessageSquare, Send, PhoneCall } from 'lucide-react';
import { Match, MatchRSVP, GroupMemberProfile, RSVPStatus } from '../types';
import { formatTimeTo12Hour, generateSmsReminderUrl, generateWhatsappReminderUrl, requestPushNotificationPermission, sendLocalPushNotification } from '../utils/timeUtils';

interface NextMatchHeroCardProps {
  match: Match;
  rsvps: MatchRSVP[];
  membersMap: Map<string, GroupMemberProfile>;
  activeMemberId: string;
  onUpdateRSVP: (status: RSVPStatus) => void;
  onViewMatch: (matchId: string) => void;
  onShareMatch: (match: Match) => void;
  onOpenPostMatchReport?: (matchId: string) => void;
}

export const NextMatchHeroCard: React.FC<NextMatchHeroCardProps> = ({
  match,
  rsvps,
  membersMap,
  activeMemberId,
  onUpdateRSVP,
  onViewMatch,
  onShareMatch,
  onOpenPostMatchReport
}) => {
  const matchRsvps = rsvps.filter(r => r.matchId === match.id);
  const goingList = matchRsvps.filter(r => r.status === 'going');
  const maybeList = matchRsvps.filter(r => r.status === 'maybe');
  const slotsLeft = Math.max(0, match.maxPlayers - goingList.length);

  const activeRsvp = matchRsvps.find(r => r.memberProfileId === activeMemberId);
  const myStatus = activeRsvp?.status || 'maybe';
  const isAwaitingReport = match.status === 'awaiting_report';

  // Find my assigned team and position if teams built
  let myTeamName = 'Not Assigned Yet';
  let myPos = 'CM';
  if (match.teamA?.players.some(p => p.memberProfileId === activeMemberId)) {
    myTeamName = match.teamA.name;
    myPos = match.teamA.players.find(p => p.memberProfileId === activeMemberId)?.position || 'CM';
  } else if (match.teamB?.players.some(p => p.memberProfileId === activeMemberId)) {
    myTeamName = match.teamB.name;
    myPos = match.teamB.players.find(p => p.memberProfileId === activeMemberId)?.position || 'CM';
  }

  // Calculate Countdown
  const [countdownText, setCountdownText] = useState('');
  const [isToday, setIsToday] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [targetPhone, setTargetPhone] = useState('+8801712345678');
  const [notifySuccessMsg, setNotifySuccessMsg] = useState('');

  const handlePushNotification = async () => {
    const granted = await requestPushNotificationPermission();
    if (granted) {
      sendLocalPushNotification(
        `⚽ GigaBalls FC: ${match.title}`,
        `Match at ${match.venueName} starts at ${formatTimeTo12Hour(match.startTime)}. Don't forget to confirm your spot!`
      );
      setNotifySuccessMsg('🔔 Push Notification sent to your device!');
      setTimeout(() => setNotifySuccessMsg(''), 4000);
    }
  };

  useEffect(() => {
    const calculateCountdown = () => {
      const matchDateTime = new Date(`${match.date}T${match.startTime}:00`);
      const now = new Date();
      const diffMs = matchDateTime.getTime() - now.getTime();

      if (diffMs <= 0 && diffMs > -3600000 * 2) {
        setCountdownText('🔴 LIVE NOW / MATCH DAY!');
        setIsToday(true);
        return;
      }

      if (diffMs <= -3600000 * 2) {
        setCountdownText('📝 RESULT & STATS NEEDED');
        setIsToday(false);
        return;
      }

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffDays === 0) {
        setIsToday(true);
        setCountdownText(`Starts in ${diffHours}h ${diffMins}m`);
      } else {
        setIsToday(false);
        setCountdownText(`Starts in ${diffDays}d ${diffHours}h`);
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000);
    return () => clearInterval(interval);
  }, [match.date, match.startTime]);

  // Formatted date string
  const formattedDate = new Date(match.date).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  return (
    <div className="relative rounded-2xl bg-[#121614] border border-white/5 p-5 md:p-6 shadow-xl overflow-hidden">
      <div className="relative z-10">
        {/* Subtle Atmospheric Glow */}
        <div className="absolute -top-12 -right-12 w-72 h-72 bg-[#a8c936]/5 rounded-full blur-3xl pointer-events-none" />

        {/* Match Header Badge & Share */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="bg-white/5 text-slate-300 text-[10px] sm:text-[11px] font-semibold px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full border border-white/5 flex items-center gap-1">
              <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#a8c936]" /> 7v7 Matchday
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-[#a8c936] bg-[#a8c936]/10 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full border border-[#a8c936]/20 font-mono">
              {countdownText}
            </span>
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            <button
              onClick={() => setShowNotifyModal(true)}
              className="flex items-center gap-1 text-[11px] sm:text-xs font-bold text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border border-amber-500/30 transition"
              title="Send Direct SMS or Push Notification"
            >
              <Bell className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400" /> Notify
            </button>
            <button
              onClick={() => onShareMatch(match)}
              className="flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border border-white/5 transition"
            >
              <Share2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Share
            </button>
          </div>
        </div>

        {/* Match Center Scoreboard & Teams Display */}
        <div className="bg-[#171c19] rounded-2xl p-3 sm:p-5 border border-white/5 mb-5 relative overflow-hidden">
          <div className="flex items-center justify-between gap-1 sm:gap-2 relative z-10 w-full">
            {/* Team A */}
            <div className="flex-1 min-w-0 flex flex-col items-center text-center">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-[#1c221d] border border-white/10 p-1.5 sm:p-2 flex items-center justify-center mb-1.5 shrink-0">
                <span className="text-xl sm:text-3xl">⚽</span>
              </div>
              <span className="text-[11px] sm:text-sm font-bold text-white truncate w-full px-0.5">
                {match.teamA?.name || 'Team Green'}
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-400 font-medium mt-0.5 truncate w-full">
                {match.teamA?.players.length || 0} players
              </span>
            </div>

            {/* Middle Match Time & Venue */}
            <div className="shrink-0 flex flex-col items-center text-center px-1 sm:px-3 min-w-[100px] sm:min-w-[130px]">
              <div className="text-[10px] sm:text-xs font-medium text-slate-400 mb-0.5">
                {formattedDate}
              </div>
              <div className="text-xl sm:text-3xl font-extrabold text-white font-mono tracking-tight">
                {formatTimeTo12Hour(match.startTime)}
              </div>
              <div className="flex items-center gap-1 text-[10px] sm:text-[12px] text-slate-300 font-medium mt-1 max-w-[110px] sm:max-w-[150px] justify-center">
                <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#a8c936] shrink-0" />
                <span className="truncate">{match.venueName}</span>
              </div>
            </div>

            {/* Team B */}
            <div className="flex-1 min-w-0 flex flex-col items-center text-center">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-[#1c221d] border border-white/10 p-1.5 sm:p-2 flex items-center justify-center mb-1.5 shrink-0">
                <span className="text-xl sm:text-3xl">🛡️</span>
              </div>
              <span className="text-[11px] sm:text-sm font-bold text-white truncate w-full px-0.5">
                {match.teamB?.name || 'Team Blue'}
              </span>
              <span className="text-[10px] sm:text-[11px] text-slate-400 font-medium mt-0.5 truncate w-full">
                {match.teamB?.players.length || 0} players
              </span>
            </div>
          </div>
        </div>

        {/* Players Capacity & Progress */}
        <div className="bg-[#171c19] rounded-2xl p-4 border border-white/5 mb-5 space-y-3">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-200 font-bold flex items-center gap-1.5">
              <Users className="w-4 h-4 text-[#a8c936]" /> Players
            </span>
            <span className="text-slate-300 font-semibold">
              <strong className="text-white">{goingList.length}</strong> of {match.maxPlayers} confirmed
            </span>
          </div>

          {/* Thin Clean Capacity Bar */}
          <div className="w-full h-2 bg-[#080b0a] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#a8c936] rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (goingList.length / match.maxPlayers) * 100)}%` }}
            />
          </div>

          {/* Player Avatars */}
          {goingList.length > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <div className="flex -space-x-2">
                {goingList.slice(0, 6).map((r) => {
                  const m = membersMap.get(r.memberProfileId);
                  return (
                    <img
                      key={r.id}
                      src={m?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                      alt={m?.displayName}
                      className="w-7 h-7 rounded-full border-2 border-[#171c19] object-cover"
                    />
                  );
                })}
              </div>
              <span className="text-[11px] text-slate-400 font-medium">
                {slotsLeft > 0 ? `${slotsLeft} spots available` : 'Roster full'}
              </span>
            </div>
          )}

          {/* Compact Info Box Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-white/5">
            <div className="bg-[#1c221d] p-2.5 rounded-xl border border-white/5">
              <span className="text-[11px] text-slate-400 block font-medium">Spots left</span>
              <span className={`font-bold text-sm ${slotsLeft <= 2 ? 'text-amber-400' : 'text-white'}`}>
                {slotsLeft > 0 ? slotsLeft : 'Waitlist'}
              </span>
            </div>
            <div className="bg-[#1c221d] p-2.5 rounded-xl border border-white/5">
              <span className="text-[11px] text-slate-400 block font-medium">Turf fee</span>
              <span className="font-bold text-sm text-white">৳{match.totalTurfFee.toLocaleString()}</span>
            </div>
            <div className="bg-[#1c221d] p-2.5 rounded-xl border border-white/5">
              <span className="text-[11px] text-slate-400 block font-medium">Per player</span>
              <span className="font-bold text-sm text-[#a8c936]">৳{match.estimatedCostPerPlayer}</span>
            </div>
            <button
              type="button"
              onClick={() => onViewMatch(match.id)}
              className="bg-[#1c221d] hover:bg-white/5 p-2.5 rounded-xl border border-white/5 transition text-left group"
              title="Click to select team and position"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400 block font-medium">My team</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-white" />
              </div>
              <span className="font-bold text-xs text-white truncate block mt-0.5">{myTeamName} • {myPos}</span>
            </button>
          </div>
        </div>

        {/* Main RSVP Action Section */}
        {isAwaitingReport ? (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">
              Match finished — publish the final report
            </h3>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-3">
              <p className="text-xs text-slate-200 leading-relaxed">
                This fixture is now out of the upcoming queue. Save the score, player stats, and MOTM to move it into completed history.
              </p>
              <div className="flex flex-wrap gap-2.5">
                <button
                  onClick={() => onOpenPostMatchReport?.(match.id)}
                  className="py-3 px-4 rounded-xl font-black text-xs sm:text-sm transition bg-amber-500 hover:bg-amber-400 text-slate-950"
                >
                  Complete post-match report
                </button>
                <button
                  onClick={() => onViewMatch(match.id)}
                  className="py-3 px-4 rounded-xl font-semibold text-xs sm:text-sm transition bg-[#171c19] hover:bg-[#1c221d] text-slate-200 border border-white/5"
                >
                  Review match details
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">
              Are you playing?
            </h3>
            <div className="grid grid-cols-3 gap-2.5">
              <button
                onClick={() => onUpdateRSVP('going')}
                className={`py-3 px-3 rounded-xl font-semibold text-xs sm:text-sm transition flex items-center justify-center gap-2 border ${
                  myStatus === 'going'
                    ? 'bg-[#1c2a19] text-[#a8c936] border-[#a8c936]/40'
                    : 'bg-[#171c19] hover:bg-[#1c221d] text-slate-300 border-white/5'
                }`}
              >
                <CheckCircle2 className={`w-4 h-4 ${myStatus === 'going' ? 'text-[#a8c936]' : 'text-slate-400'}`} />
                <span>Going</span>
              </button>

              <button
                onClick={() => onUpdateRSVP('maybe')}
                className={`py-3 px-3 rounded-xl font-semibold text-xs sm:text-sm transition flex items-center justify-center gap-2 border ${
                  myStatus === 'maybe'
                    ? 'bg-[#2b2413] text-amber-300 border-amber-500/40'
                    : 'bg-[#171c19] hover:bg-[#1c221d] text-slate-300 border-white/5'
                }`}
              >
                <AlertCircle className={`w-4 h-4 ${myStatus === 'maybe' ? 'text-amber-400' : 'text-slate-400'}`} />
                <span>Maybe</span>
              </button>

              <button
                onClick={() => onUpdateRSVP('not_going')}
                className={`py-3 px-3 rounded-xl font-semibold text-xs sm:text-sm transition flex items-center justify-center gap-2 border ${
                  myStatus === 'not_going'
                    ? 'bg-[#2b1717] text-rose-300 border-rose-500/40'
                    : 'bg-[#171c19] hover:bg-[#1c221d] text-slate-300 border-white/5'
                }`}
              >
                <span>Can't play</span>
              </button>
            </div>
          </div>
        )}

        {/* Footer Link */}
        <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
          <span className="text-xs text-slate-400 font-medium">
            {goingList.length} going • {maybeList.length} maybe
          </span>

          <button
            onClick={() => onViewMatch(match.id)}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#080b0a] bg-[#a8c936] hover:bg-[#b8db3d] px-4 py-2 rounded-xl transition"
          >
            <span>View teams</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Direct Push / Phone Notification Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-3xl p-5 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-extrabold text-base text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" /> Send Match Notification
              </h3>
              <button
                onClick={() => setShowNotifyModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {notifySuccessMsg && (
              <div className="p-3 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded-xl text-xs font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                {notifySuccessMsg}
              </div>
            )}

            <div className="space-y-3">
              {/* Option 1: Browser Push Notification */}
              <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-amber-500/10 text-amber-400 rounded-xl">
                      <Bell className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-extrabold text-white">Browser Push Notification</h4>
                      <p className="text-[10px] text-slate-400">Trigger instant alert on this device</p>
                    </div>
                  </div>
                  <button
                    onClick={handlePushNotification}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black rounded-xl transition"
                  >
                    Send Push 🔔
                  </button>
                </div>
              </div>

              {/* Option 2: Direct SMS to Phone Number */}
              <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-500/10 text-blue-400 rounded-xl">
                    <PhoneCall className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-extrabold text-white">Direct SMS Message</h4>
                    <p className="text-[10px] text-slate-400">Sends match info via SMS app</p>
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="text-[10px] text-slate-400 font-bold block">Target Phone Number</label>
                  <input
                    type="tel"
                    value={targetPhone}
                    onChange={(e) => setTargetPhone(e.target.value)}
                    placeholder="+880 1712 345678"
                    className="w-full bg-slate-900 border border-slate-800 text-white p-2 rounded-xl text-xs font-mono"
                  />
                  
                  <a
                    href={generateSmsReminderUrl(
                      targetPhone,
                      match.title,
                      match.date,
                      match.startTime,
                      match.venueName
                    )}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs rounded-xl transition flex items-center justify-center gap-1.5 mt-2"
                  >
                    <Send className="w-3.5 h-3.5" /> Send Direct SMS
                  </a>
                </div>
              </div>

              {/* Option 3: WhatsApp Direct Message */}
              <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-emerald-500/10 text-emerald-400 rounded-xl">
                    <MessageSquare className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-extrabold text-white">WhatsApp Direct Message</h4>
                    <p className="text-[10px] text-slate-400">Opens WhatsApp chat with prefilled alert</p>
                  </div>
                </div>

                <a
                  href={generateWhatsappReminderUrl(
                    targetPhone,
                    match.title,
                    match.date,
                    match.startTime,
                    match.venueName
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl transition flex items-center justify-center gap-1.5 mt-1"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Open WhatsApp Direct Message
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
